import { notification } from 'antd';
import {
    createDestination,
    updateDestination,
    bulkCreateUpdateDestinationContents,
    bulkCreateUpdateDestinationContentTranslations,
} from 'api/destinations.api';
import { DestinationContentSources } from 'constants/destination-content-sources';
import { DestinationContentTypes } from 'constants/destination-content-types';
import { DestinationGeoType } from 'constants/destination-geo-type';
import { DestinationMapVisibility } from 'constants/destination-map-visibility';
import { DestinationPopularity } from 'constants/destination-popularity';
import { DestinationStatuses } from 'constants/destination-statuses';
import { unionBy } from 'lodash';
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { DestinationContentModifySchema } from 'typings/destinations/DestinationContentModifySchema';
import type { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import { DestinationContentTranslationModifySchema } from 'typings/destinations/DestinationContentTranslationModifySchema';
import { DestinationContentTranslationSchema } from 'typings/destinations/DestinationContentTranslationSchema';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import { DestinationGeoTypeEnum } from 'typings/destinations/DestinationGeoTypeEnum';

export type Destination = Omit<DestinationSchema, 'id'> & {
    id?: number;
};

export type DestinationContent = Omit<DestinationContentSchema, 'id'> & {
    id?: number;
};

const createEmptyDestination = (
    geoType: DestinationGeoTypeEnum = DestinationGeoType.PLACE,
): Destination => ({
    id: 0,
    guide_id: null,
    destination_media_id: null,
    cover_image_id: null,
    cover_image: null,
    name: null,
    boundary_area: null,
    administrative_area: null,
    country: null,
    country_code: null,
    lat: null,
    lon: null,
    status: DestinationStatuses.DRAFT,
    map_visibility: DestinationMapVisibility.APPEARS_LAST,
    popularity: DestinationPopularity.RARELY_EXPLORED,
    geo_type: geoType,
    contents: [
        {
            id: 0,
            content_type: DestinationContentTypes.TAGLINE,
            body: '',
            order: 0,
            source: DestinationContentSources.MANUAL,
        },
        {
            id: 0,
            content_type: DestinationContentTypes.DESCRIPTION,
            body: '',
            order: 0,
            source: DestinationContentSources.MANUAL,
        },
    ],
    visitors_t1: null,
    visitors_t2: null,
});

type SaveBulkContentParams = {
    destinationId: number;
    contents: (DestinationContentModifySchema & {
        id?: number;
        translations?: (DestinationContentTranslationModifySchema & {
            id?: number;
        })[];
    })[];
};

type SaveDestinationParams = {
    destination: Destination;
    contents: DestinationContent[];
};

type DestinationEditorContextProps = {
    destination: Destination;
    location: PSLocation | null;
    updateDestinationField: <T>(
        fieldName: keyof Destination,
        newValue: T,
    ) => void;
    updateLocation: (location: PSLocation) => void;
    updateDestination: React.Dispatch<React.SetStateAction<Destination>>;
    resetEditor: () => void;
    saveDestination: (params: SaveDestinationParams) => Promise<void>;
    saveBulkContent: (
        params: SaveBulkContentParams,
    ) => Promise<DestinationContentSchema[]>;
    isEditing: boolean;
    isCountry: boolean;
    locale: string;
    setLocale: React.Dispatch<React.SetStateAction<string>>;
    isContentCurated: boolean;
    hasContent: boolean;
};

export type UpdateDestinationContentFieldChange = {
    id: Id;
    fieldName: keyof DestinationContentSchema;
    newValue: DestinationContentSchema[keyof DestinationContentSchema];
};

const DestinationEditorContext = createContext<
    DestinationEditorContextProps | undefined
>(undefined);

export const useDestinationEditor = () => {
    const context = useContext(DestinationEditorContext);
    if (!context) {
        throw new Error(
            'useDestinationEditor must be used within a DestinationEditorProvider',
        );
    }
    return context;
};

type DestinationEditorProviderProps = Readonly<{
    destination?: Destination | null;
    initialGeoType?: DestinationGeoTypeEnum;
    children: ReactNode;
}>;

function DestinationEditorProvider({
    destination,
    initialGeoType = DestinationGeoType.PLACE,
    children,
}: DestinationEditorProviderProps) {
    const [destinationState, setDestinationState] = useState<Destination>(
        destination ?? createEmptyDestination(initialGeoType),
    );
    const [locale, setLocale] = useState<string>('en');
    const navigate = useNavigate();
    const isEditing = !!destinationState?.id;
    const isCountry = destinationState?.geo_type === DestinationGeoType.COUNTRY;
    const location: PSLocation | null = useMemo(() => {
        return destinationState && destinationState.locality
            ? {
                  name: destinationState.locality ?? '',
                  countryCode: destinationState.country_code ?? '',
                  detail: destinationState.country ?? '',
                  fullDetail: destinationState.country ?? '',
                  lat: destinationState.lat ?? 0,
                  lon: destinationState.lon ?? 0,
              }
            : null;
    }, [destinationState]);
    const hasContent = useMemo(() => {
        return Boolean(
            destinationState.contents?.some(
                (content) =>
                    (content.title && content.title.trim() !== '') ||
                    (content.body && content.body.trim() !== ''),
            ),
        );
    }, [destinationState.contents]);
    const isContentCurated = useMemo(() => {
        if (!hasContent) {
            return false;
        }

        return (
            destinationState.contents?.every(
                (content) =>
                    content.source === DestinationContentSources.MANUAL ||
                    content.source === DestinationContentSources.FROM_GUIDES,
            ) ?? false
        );
    }, [destinationState.contents, hasContent]);

    const updateDestinationField = useCallback(
        <T,>(fieldName: keyof Destination, newValue: T) => {
            setDestinationState((prevDestination) =>
                prevDestination
                    ? { ...prevDestination, [fieldName]: newValue }
                    : prevDestination,
            );
        },
        [],
    );

    const updateLocation = useCallback((location: PSLocation) => {
        setDestinationState((prevDestination) =>
            prevDestination
                ? {
                      ...prevDestination,
                      locality: location.name,
                      country: location.detail,
                      country_code: location.countryCode,
                      lat: location.lat,
                      lon: location.lon,
                  }
                : prevDestination,
        );
    }, []);

    const resetEditor = useCallback(() => {
        setDestinationState(createEmptyDestination());
    }, []);

    // Helper function to find original content matching saved content
    const findMatchingOriginalContent = (
        savedContent: DestinationContentSchema,
        originalContents: SaveBulkContentParams['contents'],
    ) => {
        return originalContents.find((c) => {
            if (savedContent.id && c.id && c.id > 0) {
                return c.id === savedContent.id;
            }
            return (
                c.content_type === savedContent.content_type &&
                c.order === savedContent.order
            );
        });
    };

    // Helper function to prepare translations with correct IDs
    const prepareTranslationsForSave = (
        translationsToSave: (DestinationContentTranslationModifySchema & {
            id?: number;
        })[],
        existingTranslations: DestinationContentTranslationSchema[],
    ) => {
        return translationsToSave.map((translation) => {
            // Find existing translation by language
            const existing = existingTranslations.find(
                (et) => et.language === translation.language,
            );

            // Use existing ID if found, otherwise it's a new translation
            if (existing?.id) {
                return {
                    id: existing.id,
                    ...translation,
                };
            }

            // New translation - don't include ID
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, ...translationData } = translation;
            return translationData;
        });
    };

    // Helper function to save translations for a single content
    const saveContentTranslations = useCallback(
        async (
            savedContent: DestinationContentSchema,
            originalContent:
                | SaveBulkContentParams['contents'][number]
                | undefined,
        ): Promise<DestinationContentSchema> => {
            // Handle case where originalContent has no translations
            if (
                !originalContent?.translations ||
                originalContent.translations.length === 0
            ) {
                return {
                    ...savedContent,
                    translations: savedContent.translations || [],
                };
            }

            const translationsToSave = prepareTranslationsForSave(
                originalContent.translations,
                savedContent.translations || [],
            );

            try {
                const savedTranslations = await bulkCreateUpdateDestinationContentTranslations(
                    savedContent.id,
                    translationsToSave,
                );

                return {
                    ...savedContent,
                    translations: savedTranslations,
                };
            } catch (error) {
                console.error(
                    `Error saving translations for content ${savedContent.id}:`,
                    error,
                );
                return {
                    ...savedContent,
                    translations: savedContent.translations || [],
                };
            }
        },
        [],
    );

    const saveBulkContent = useCallback(
        async ({
            destinationId,
            contents,
        }: SaveBulkContentParams): Promise<DestinationContentSchema[]> => {
            try {
                // Prepare contents for bulk save - remove negative IDs and translations field
                const contentsToSave = contents.map(
                    ({ translations: _translations, ...content }) => {
                        const contentData = { ...content };
                        if (contentData.id && contentData.id <= 0) {
                            contentData.id = undefined;
                        }
                        return contentData;
                    },
                );

                // Bulk save all contents
                const savedContents = await bulkCreateUpdateDestinationContents(
                    destinationId,
                    contentsToSave,
                );

                // Save translations for each content
                const contentsWithTranslations = await Promise.all(
                    savedContents.map(async (savedContent) => {
                        const originalContent = findMatchingOriginalContent(
                            savedContent,
                            contents,
                        );
                        return saveContentTranslations(
                            savedContent,
                            originalContent,
                        );
                    }),
                );

                return contentsWithTranslations;
            } catch (error) {
                notification.error({
                    message: 'Error saving content',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                });
                throw error;
            }
        },
        [saveContentTranslations],
    );

    const saveDestination = useCallback(
        async ({
            destination: destinationToSave,
            contents,
        }: SaveDestinationParams) => {
            try {
                const updatedDestination = destinationToSave.id
                    ? await updateDestination(
                          destinationToSave.id,
                          destinationToSave,
                      )
                    : await createDestination(destinationToSave);

                // Filter out empty contents that are new (no ID)
                // Keep existing contents even if empty (so they can be updated/deleted)
                const contentsToSave = contents.filter(
                    (content) =>
                        (content.body && content.body.trim().length > 0) ||
                        (content.id && content.id > 0),
                );

                // Only save if there are non-empty contents
                let savedContents: DestinationContentSchema[] = [];

                if (contentsToSave.length > 0) {
                    savedContents = await saveBulkContent({
                        destinationId: updatedDestination.id,
                        contents: contentsToSave.map((content) => {
                            const contentId =
                                content.id && content.id > 0
                                    ? content.id
                                    : undefined;
                            return {
                                ...content,
                                id: contentId,
                                translations: content.translations,
                            };
                        }),
                    });
                }

                // Filter to only get the content types we sent (API returns all contents)
                const contentTypes = new Set(
                    contents.map((c) => c.content_type),
                );
                const filteredContents = savedContents.filter((content) =>
                    contentTypes.has(content.content_type),
                );

                setDestinationState({
                    ...updatedDestination,
                    contents: mergeContentsByField(
                        updatedDestination.contents ?? [],
                        filteredContents,
                        'id',
                    ),
                });

                if (!isEditing) {
                    navigate(`/destinations/${updatedDestination.id}`, {
                        replace: true,
                    });
                }

                notification.success({
                    message: 'Destination saved successfully',
                    key: 'destination-saved',
                });
            } catch (error) {
                notification.error({
                    message: 'Error saving destination',
                    description:
                        error instanceof Error
                            ? error.message
                            : 'Unknown error',
                });
            }
        },
        [navigate, isEditing, saveBulkContent],
    );

    const value: DestinationEditorContextProps = useMemo(
        () => ({
            destination: destinationState,
            location,
            updateDestinationField,
            updateLocation,
            resetEditor,
            saveDestination,
            saveBulkContent,
            updateDestination: setDestinationState,
            isEditing,
            isCountry,
            locale,
            setLocale,
            isContentCurated,
            hasContent,
        }),
        [
            destinationState,
            location,
            updateDestinationField,
            updateLocation,
            resetEditor,
            saveDestination,
            saveBulkContent,
            setDestinationState,
            isEditing,
            isCountry,
            locale,
            setLocale,
            isContentCurated,
            hasContent,
        ],
    );

    return (
        <DestinationEditorContext.Provider value={value}>
            {children}
        </DestinationEditorContext.Provider>
    );
}

export default DestinationEditorProvider;

export function mergeContentsByField(
    originalContents: DestinationContentSchema[],
    updatedContents: DestinationContentSchema[],
    fieldToMerge: keyof DestinationContentSchema,
): DestinationContentSchema[] {
    return unionBy(updatedContents, originalContents, fieldToMerge);
}
